'use client'
import { useState, useMemo } from 'react'
import { ArrowDown, ArrowUp, FileText, AlertTriangle, History, TrendingUp } from 'lucide-react'
import clsx from 'clsx'
import Button from '@/components/ui/Button'
import { ESMStockListItemFE, PriceData } from '@/types/esmStock'
import ESMStockDetailModal from './ESMStockDetailModal'
import StockActionIcons from '@/components/stocks/StockActionIcons'

type SortField =
  | 'stock_name'
  | 'current_state'
  | 'current_state_date'
  | 'current_state_transaction_type'
  | 'days_spent_in_current_state'
  | 'days_spent_in_esm'
  | 'predicted_transition_date'
  | 'prediction_type'
  | 'current_market_price'
  | 'market_cap'
  | 'price_to_earning'

type SortDirection = 'asc' | 'desc'

const formatDate = (s?: string | null): string =>
  s
    ? new Date(s).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' })
    : 'N/A'

const formatTxnType = (t?: string | null): string => {
  if (!t) return 'N/A'
  
  if (t === 'moved_up') return 'ESM 1->2'
  if (t === 'moved_down') return 'ESM 2->1'
  
  return t.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

const formatNullableNumber = (n?: number | null): string | number =>
  n != null ? n : 'N/A'

const formatCurrency = (n?: number | null): string => {
  if (n == null) return 'N/A';
  const isNegative = n < 0;
  const absValue = Math.abs(n);
  const sign = isNegative ? '-' : '';
  return `${sign}₹${absValue.toFixed(2)}`;
}

const formatMarketCap = (n?: number | null): string => {
  if (n == null) return 'N/A';
  const isNegative = n < 0;
  const absValue = Math.abs(n);
  const sign = isNegative ? '-' : '';
  return `${sign}₹${absValue.toFixed(2)} Cr`;
}

const formatPercentage = (n?: number | null): string =>
  n != null ? `${n.toFixed(2)}%` : 'N/A'

export default function ESMStockTable({ stocks }: { stocks: ESMStockListItemFE[] }) {
  const [sortField, setSortField] = useState<SortField>('current_state_date')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [selectedStock, setSelectedStock] = useState<string | null>(null)
  
  // Check if we're in prediction mode by looking at the first stock
  const isPredictionMode = useMemo(() => 
    stocks.length > 0 && stocks[0].prediction_type != null, 
    [stocks]
  )

  const handleSort = (field: SortField) => {
    const dir = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc'
    setSortField(field)
    setSortDirection(dir)
  }

  const handleStockClick = (isin: string) => {
    setSelectedStock(isin)
  }

  const handleCloseModal = () => {
    setSelectedStock(null)
  }

  const sorted = useMemo(() => {
    const copy = [...stocks]
    return copy.sort((a, b) => {
      const av = a[sortField] as string | number | null
      const bv = b[sortField] as string | number | null
      const m = sortDirection === 'asc' ? 1 : -1
      if (av == null && bv == null) return 0
      if (av == null) return 1 * m
      if (bv == null) return -1 * m
      if (sortField.endsWith('_date')) {
        const ad = new Date(av as string).getTime()
        const bd = new Date(bv as string).getTime()
        if (isNaN(ad) && isNaN(bd)) return 0
        if (isNaN(ad)) return 1 * m
        if (isNaN(bd)) return -1 * m
        return (ad - bd) * m
      }
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * m
      if (typeof av === 'string' && typeof bv === 'string') return av.localeCompare(bv) * m
      return av < bv ? -1 * m : av > bv ? 1 * m : 0
    })
  }, [stocks, sortField, sortDirection])

  const SortIcon = ({ field }: { field: SortField }) =>
    sortField === field ? (
      sortDirection === 'asc' ? <ArrowUp size={16} className="ml-1 text-emerald-500" /> : <ArrowDown size={16} className="ml-1 text-emerald-500" />
    ) : (
      <span className="ml-1 text-zinc-400 opacity-30 hover:opacity-70 transition-opacity">⇅</span>
    )

  const buildCsv = (rows: ESMStockListItemFE[]): string => {
    // Base header fields
    const baseHeader = [
      'ISIN','Stock Name','NSE Code','BSE Code','Stock Code','TradingView','Screener','Current State','Current State Date','Transaction Type',
      'Days in State','Days in ESM','Current Price','Market Cap (Cr)','P/E Ratio'
    ]
    
    // Prediction mode specific fields
    const predictionHeader = [
      'Prediction Type','Predicted Date','Prediction Reason',
      'ESM Price','ESM Price Date','Price Change %'
    ]
    
    // History specific fields
    const historyHeader = [
      'Second Last State','Second Last State Date','Second Last Txn Type'
    ]
    
    const header = isPredictionMode 
      ? [...baseHeader, ...predictionHeader, 'External Code']
      : [...baseHeader, 'Prediction Type','Predicted Date','Prediction Reason', ...historyHeader, 'External Code']
    
    const body = rows.map(s => {
      // Base row data
      const baseRow = [
        s.isin,
        `"${s.stock_name?.replace(/"/g, '""')}"`,
        s.nse_code ?? '',
        s.bse_code ?? '',
        s.stock_code ?? '',
        s.trading_view_navlink ?? 'N/A',
        s.screener_navlink ?? 'N/A',
        s.current_state ?? 'N/A',
        s.current_state_date ?? 'N/A',
        s.current_state_transaction_type ?? 'N/A',
        s.days_spent_in_current_state ?? 'N/A',
        s.days_spent_in_esm ?? 'N/A',
        s.current_market_price ?? 'N/A',
        s.market_cap ?? 'N/A',
        s.price_to_earning ?? 'N/A'
      ]
      
      // Basic prediction data
      const predictionBasicRow = [
        s.prediction_type ?? 'N/A',
        s.predicted_transition_date ?? 'N/A',
        `"${s.reason?.replace(/"/g, '""')}"`
      ]
      
      // Price data for prediction mode
      const priceDataRow = (s as ESMStockListItemFE & { price_data?: PriceData }).price_data ? [
        (s as ESMStockListItemFE & { price_data?: PriceData }).price_data?.esm_state_price ?? 'N/A',
        (s as ESMStockListItemFE & { price_data?: PriceData }).price_data?.esm_state_date ?? 'N/A',
        (s as ESMStockListItemFE & { price_data?: PriceData }).price_data?.price_change_percentage ?? 'N/A'
      ] : ['N/A', 'N/A', 'N/A']
      
      // History data
      const historyRow = [
        s.second_last_state ?? 'N/A',
        s.second_last_state_date ?? 'N/A',
        s.second_last_state_transaction_type ?? 'N/A'
      ]
      
      // External code at the end
      const externalCodeRow = s.stock_code ? `"${s.stock_code},"` : ''
      
      return isPredictionMode
        ? [...baseRow, ...predictionBasicRow, ...priceDataRow, externalCodeRow].join(',')
        : [...baseRow, ...predictionBasicRow, ...historyRow, externalCodeRow].join(',')
    }).join('\n')
    
    return [header.join(','), body].join('\n')
  }

  const triggerDownload = (content: string, fname: string, mime: string) => {
    const blob = new Blob([content], { type: mime })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fname
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="w-full table-container-with-popups">
      <div className="flex justify-end mb-4 gap-2">
        <Button variant="outline" onClick={() => triggerDownload(buildCsv(sorted), `esm-stocks-${new Date().toISOString().slice(0,10)}.csv`, 'text/csv')} disabled={!stocks.length}>
          <FileText size={16} className="mr-1" />CSV
        </Button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-zinc-200 dark:border-zinc-800 table-wrapper-popup-safe min-table-height">
        <table className="min-w-full divide-y divide-zinc-200 dark:divide-zinc-800 text-sm">
          <thead className="bg-zinc-50 dark:bg-zinc-800/60 text-xs uppercase font-medium tracking-wider">
            <tr>
              {[
                { label: 'Stock Name', field: 'stock_name' },
                { label: 'Current State', field: 'current_state' },
                { label: 'State Date', field: 'current_state_date' },
                { label: 'Transaction', field: 'current_state_transaction_type' },
                { label: 'Current Price', field: 'current_market_price' },
                { label: 'Market Cap', field: 'market_cap' },
                { label: 'P/E Ratio', field: 'price_to_earning' },
                { label: 'Days in State', field: 'days_spent_in_current_state' },
                // Only show Prediction column for prediction mode
                ...(isPredictionMode 
                  ? [{ label: 'Prediction', field: 'predicted_transition_date' as SortField }]
                  : []
                ),
                // Only show Price Data for prediction mode, History for historical mode
                ...(isPredictionMode 
                  ? [{ label: 'Price Data', field: 'price_data' as SortField }] 
                  : [{ label: 'History', field: 'second_last_state_date' }]
                )
              ].map(({ label, field }) => (
                <th key={field} className="px-4 py-3 text-left whitespace-nowrap cursor-pointer select-none text-zinc-500 dark:text-zinc-400" onClick={() => handleSort(field as SortField)}>
                  <div className="flex items-center">{label}<SortIcon field={field as SortField} /></div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {!sorted.length ? (
              <tr><td colSpan={isPredictionMode ? 11 : 10} className="text-center py-10 text-zinc-500 dark:text-zinc-400">No data available.</td></tr>
            ) : sorted.map(s => (
              <tr key={s.isin} className={clsx('hover:bg-zinc-50 dark:hover:bg-zinc-800/50 table-row-with-menu', s.prediction_type && 'bg-amber-50 dark:bg-amber-900/10')}>
                <td className="px-4 py-3 align-top">
                  <div className="flex-1">
                    <StockActionIcons
                      isin={s.isin}
                      stockName={s.stock_name}
                      tradingViewLink={s.trading_view_navlink || undefined}
                      screenerLink={s.screener_navlink || undefined}
                      currentPrice={s.current_market_price || undefined}
                      showNotesCount
                    />
                    <div 
                      className="font-medium text-zinc-900 dark:text-white cursor-pointer hover:text-emerald-600 dark:hover:text-emerald-400"
                      onClick={() => handleStockClick(s.isin)}
                    >
                      {s.stock_name}
                    </div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400">{s.isin}</div>
                  </div>
                </td>
                <td className="px-4 py-3 align-top text-center">{formatNullableNumber(s.current_state)}</td>
                <td className="px-4 py-3 align-top whitespace-nowrap">{formatDate(s.current_state_date)}</td>
                <td className="px-4 py-3 align-top"><span className={clsx('px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap', {
                  'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200': s.current_state_transaction_type==='entry',
                  'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200': s.current_state_transaction_type==='exit',
                  'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200': s.current_state_transaction_type==='moved_up',
                  'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200': s.current_state_transaction_type==='moved_down',
                  'bg-zinc-100 text-zinc-800 dark:bg-zinc-700 dark:text-zinc-200': !['entry','exit','moved_up','moved_down'].includes(s.current_state_transaction_type||'')
                })}>{formatTxnType(s.current_state_transaction_type)}</span></td>
                <td className="px-4 py-3 align-top text-right">{formatCurrency(s.current_market_price)}</td>
                <td className="px-4 py-3 align-top text-right">{formatMarketCap(s.market_cap)}</td>
                <td className="px-4 py-3 align-top text-center">{formatNullableNumber(s.price_to_earning)}</td>
                <td className="px-4 py-3 align-top text-center">{formatNullableNumber(s.days_spent_in_current_state)}</td>
                <td className="px-4 py-3 align-top text-center">{formatNullableNumber(s.days_spent_in_esm)}</td>
                
                {/* Prediction column - only show in prediction mode */}
                {isPredictionMode && (
                  <td className="px-4 py-3 align-top text-xs">{s.prediction_type ? <div className="flex flex-col gap-0.5"><div className="flex items-center gap-1 font-semibold text-amber-700 dark:text-amber-400"><AlertTriangle size={14}/><span>{formatTxnType(s.prediction_type)}</span></div><div className="text-zinc-600 dark:text-zinc-400">on: {formatDate(s.predicted_transition_date)}</div><div className="text-zinc-500 dark:text-zinc-500 mt-0.5 italic">{s.reason}</div></div> : <span className="text-zinc-400 dark:text-zinc-500 italic select-none">—</span>}</td>
                )}
                
                {/* Show Price Data for prediction mode, History for historical mode */}
                {isPredictionMode ? (
                  <td className="px-4 py-3 align-top text-xs">
                    {(s as ESMStockListItemFE & { price_data?: PriceData }).price_data ? (
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-1 font-semibold text-zinc-800 dark:text-zinc-200">
                          <TrendingUp size={14}/>
                          <span>Price Change: {formatPercentage((s as ESMStockListItemFE & { price_data?: PriceData }).price_data?.price_change_percentage)}</span>
                        </div>
                        <div className="text-zinc-600 dark:text-zinc-400">
                          ESM Price: {formatCurrency((s as ESMStockListItemFE & { price_data?: PriceData }).price_data?.esm_state_price)}
                        </div>
                        <div className="text-zinc-600 dark:text-zinc-400">
                          ESM Date: {formatDate((s as ESMStockListItemFE & { price_data?: PriceData }).price_data?.esm_state_date)}
                        </div>
                        <div className="text-zinc-600 dark:text-zinc-400">
                          Current: {formatCurrency((s as ESMStockListItemFE & { price_data?: PriceData }).price_data?.current_price)}
                        </div>
                      </div>
                    ) : (
                      <span className="text-zinc-400 dark:text-zinc-500 italic select-none">—</span>
                    )}
                  </td>
                ) : (
                  <td className="px-4 py-3 align-top text-xs text-zinc-500 dark:text-zinc-400">
                    {s.second_last_state_date ? (
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-1">
                          <History size={14}/>
                          <span>{formatDate(s.second_last_state_date)}</span>
                        </div>
                        <div>State: {formatNullableNumber(s.second_last_state)}</div>
                        <div>Type: {formatTxnType(s.second_last_state_transaction_type)}</div>
                      </div>
                    ) : (
                      <span className="text-zinc-400 dark:text-zinc-500 italic select-none">—</span>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail Modal */}
      {selectedStock && (
        <ESMStockDetailModal 
          isin={selectedStock} 
          onClose={handleCloseModal}
        />
      )}
    </div>
  )
}